<script setup lang="ts">
import { router } from "@inertiajs/vue3"
import { computed, ref, watch } from "vue"

import SlideoverFooter from "@/components/SlideoverFooter.vue"
import { ICON } from "@/lib/icons"
import { itemLoansPath } from "@/routes"
import type { PersonOption } from "@/types/models"

const props = defineProps<{
  open: boolean
  itemId: string
  people: PersonOption[]
}>()

const emit = defineEmits<{
  "update:open": [value: boolean]
}>()

const direction = ref<"lent" | "borrowed">("lent")
const personId = ref<string | undefined>(undefined)
const loanedOn = ref("")
const expectedReturnOn = ref("")
const notes = ref("")
const saving = ref(false)
const formErrors = ref<Record<string, string>>({})

watch(
  () => props.open,
  (open) => {
    if (open) {
      direction.value = "lent"
      personId.value = undefined
      const now = new Date()
      loanedOn.value = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`
      expectedReturnOn.value = ""
      notes.value = ""
      formErrors.value = {}
    }
  },
)

function close() {
  emit("update:open", false)
}

const directionItems = [
  { label: "Lent out", value: "lent", icon: ICON.lentOut },
  { label: "Borrowed", value: "borrowed", icon: ICON.borrowedIn },
]

const peopleItems = computed(() =>
  props.people.map((p) => ({
    label: p.name,
    value: p.id,
  })),
)

function submit() {
  saving.value = true
  router.post(
    itemLoansPath(props.itemId),
    {
      loan: {
        direction: direction.value,
        person_id: personId.value,
        loaned_on: loanedOn.value,
        expected_return_on: expectedReturnOn.value || null,
        notes: notes.value || null,
      },
    },
    {
      onSuccess: () => close(),
      onError: (errors) => (formErrors.value = errors),
      onFinish: () => (saving.value = false),
    },
  )
}
</script>

<template>
  <USlideover :open="open" title="Record a Loan" @update:open="emit('update:open', $event)">
    <template #body>
      <form id="loan-form" class="flex flex-col gap-6" @submit.prevent="submit">
        <UAlert
          v-if="formErrors.base"
          :title="formErrors.base"
          color="error"
          variant="subtle"
          icon="i-lucide-circle-alert"
        />

        <UFormField label="Direction" required>
          <URadioGroup
            v-model="direction"
            :items="directionItems"
            value-key="value"
            orientation="horizontal"
          />
        </UFormField>

        <UFormField label="Person" required :error="formErrors.person_id">
          <USelectMenu
            v-model="personId"
            :items="peopleItems"
            value-key="value"
            placeholder="Select a person..."
            class="w-full"
          />
        </UFormField>

        <UFormField label="Date Loaned" required :error="formErrors.loaned_on">
          <UInput v-model="loanedOn" type="date" class="w-full" />
        </UFormField>

        <UFormField label="Expected Return">
          <UInput v-model="expectedReturnOn" type="date" class="w-full" />
        </UFormField>

        <UFormField label="Notes">
          <UTextarea v-model="notes" placeholder="Optional notes..." :rows="3" class="w-full" />
        </UFormField>
      </form>
    </template>

    <template #footer>
      <SlideoverFooter
        form-id="loan-form"
        submit-label="Record Loan"
        :loading="saving"
        @cancel="close"
      />
    </template>
  </USlideover>
</template>
